import { dbApiUrl, DEFAULT_SENSOR, fetchDbApi } from './dbApi';

export { dbApiUrl, DEFAULT_SENSOR, fetchDbApi, isDbApiDebugEnabled } from './dbApi';
export {
  salifertSyringeDkh,
  salifertDropsDkh,
  fetchAlkTests,
  saveAlkTest,
  fetchSalifertCatalog,
  fetchSalifertTests
} from './salifert';

/**
 * Latest row + ATO-related event count for today (MySQL `server/api/dashboard_snapshot.php`).
 * @param {{ sensorId?: string }} opts
 */
export async function fetchDashboardSnapshot(opts = {}) {
  const sensorId = opts.sensorId ?? DEFAULT_SENSOR;
  const params = new URLSearchParams({ sensor_id: sensorId });
  const res = await fetchDbApi(`${dbApiUrl('api/dashboard_snapshot.php')}?${params.toString()}`, {
    headers: { Accept: 'application/json' }
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * Aggregated series for reef status chart (MySQL `server/api/reef_chart_series.php`).
 * @param {{ range?: 'hour'|'day'|'week'|'month', sensorId?: string }} opts
 */
export async function fetchReefChartSeries(opts = {}) {
  const { range = 'day', sensorId } = opts;
  const params = new URLSearchParams({
    sensor_id: sensorId ?? DEFAULT_SENSOR,
    range
  });
  const res = await fetchDbApi(`${dbApiUrl('api/reef_chart_series.php')}?${params.toString()}`, {
    headers: { Accept: 'application/json' }
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

async function jsonFromResponse(res) {
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
  return data;
}

export async function reefTanks() {
  const res = await fetchDbApi(dbApiUrl('api/reef/tanks.php'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function reefSettingsBootstrap() {
  const res = await fetchDbApi(dbApiUrl('api/reef/settings.php'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function reefCreateTank(body) {
  const res = await fetchDbApi(dbApiUrl('api/reef/tanks.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function reefUpdateTank(body) {
  const res = await fetchDbApi(dbApiUrl('api/reef/tanks.php'), {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function reefDeleteTank(id) {
  const res = await fetchDbApi(dbApiUrl('api/reef/tanks.php'), {
    method: 'DELETE',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ id })
  });
  return jsonFromResponse(res);
}

/**
 * List reef equipment for the logged-in user.
 * @param {{ tankId?: string }} [opts]
 */
export async function reefEquipment(opts = {}) {
  const params = new URLSearchParams();
  if (opts.tankId) {
    params.set('tank_id', opts.tankId);
  }
  const q = params.toString();
  const res = await fetchDbApi(dbApiUrl(`api/reef/equipment.php${q ? `?${q}` : ''}`), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function reefCreateEquipment(body) {
  const res = await fetchDbApi(dbApiUrl('api/reef/equipment.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function reefUpdateEquipment(body) {
  const res = await fetchDbApi(dbApiUrl('api/reef/equipment.php'), {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function reefDeleteEquipment(id) {
  const res = await fetchDbApi(dbApiUrl('api/reef/equipment.php'), {
    method: 'DELETE',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ id })
  });
  return jsonFromResponse(res);
}

/**
 * List reef livestock for the logged-in user.
 * @param {{ tankId?: string }} [opts]
 */
export async function reefLivestock(opts = {}) {
  const params = new URLSearchParams();
  if (opts.tankId) {
    params.set('tank_id', opts.tankId);
  }
  const q = params.toString();
  const res = await fetchDbApi(dbApiUrl(`api/reef/livestock.php${q ? `?${q}` : ''}`), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function reefCreateLivestock(body) {
  const res = await fetchDbApi(dbApiUrl('api/reef/livestock.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function reefUpdateLivestock(body) {
  const res = await fetchDbApi(dbApiUrl('api/reef/livestock.php'), {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function reefDeleteLivestock(id) {
  const res = await fetchDbApi(dbApiUrl('api/reef/livestock.php'), {
    method: 'DELETE',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ id })
  });
  return jsonFromResponse(res);
}
